import * as React from 'react';
import { Typography, Box, Button, Grid, Paper, Chip, Card, CardContent, CircularProgress } from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, notification } from 'antd';
import { isMobile } from 'react-device-detect';
import { isNumber } from 'lodash';
import QrReader from 'modern-react-qr-reader';
import { getBazaar, sendBazaarStock } from '../../app/store/actions/imsAdminActions';
import { MainInput, main_button, sec_button, left_flex_box } from '../../app/components/Styles';
import { getRequest } from '../../core/network';
import { ArrowBack } from '@mui/icons-material';
import endpoints from '../../constants/endPoints.json';

const parseProductScanInput = (value = '') => {
  try {
    const json = JSON.parse(value);
    if (!isNumber(json) && json?.productId) {
      return { productId: String(json.productId) };
    }
  } catch (err) {
    // raw barcode / SKU
  }

  const trimmed = value.trim();
  return trimmed ? { sku: trimmed } : null;
};

export const BazaarSendStock = ({ getBazaar, sendBazaarStock, bazaarData, token, isWaiting }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [skuInput, setSkuInput] = React.useState('');
  const [sendItems, setSendItems] = React.useState([]);
  const [gettingItem, setGettingItem] = React.useState(false);
  const [scanning, setScanning] = React.useState(false);

  React.useEffect(() => { getBazaar(id); }, [id]);

  const bazaar = bazaarData?.bazaar;
  const mainWarehouseStock = bazaarData?.mainWarehouseStock || {};

  const requestedMap = React.useMemo(() => {
    const map = new Map();
    (bazaar?.lineItems || []).forEach((li) => {
      map.set(String(li.product?._id || li.product), li.quantityRequested);
    });
    return map;
  }, [bazaar]);

  const getAvailableStock = (productId) => mainWarehouseStock[String(productId)] ?? 0;

  const addProductToSendList = (product) => {
    const defaultWh = product.partner?.defaultWarehouse;
    const mainStockEntry = (product.stocks || []).find((s) =>
      String(s.warehouse?._id || s.warehouse) === String(defaultWh)
    );
    const availableStock = mainStockEntry?.amount ?? getAvailableStock(product._id);
    if (availableStock <= 0) {
      notification.warning({ message: `No stock available for ${product.sku || product.name}` });
      return false;
    }

    const existing = sendItems.find((i) => i._id === product._id);
    if (existing) {
      const nextQty = Math.min(existing.quantitySent + 1, existing.availableStock);
      if (nextQty === existing.quantitySent) {
        notification.warning({ message: `Only ${existing.availableStock} available in main warehouse` });
        return false;
      }
      setSendItems((prev) => prev.map((i) => (
        i._id === product._id ? { ...i, quantitySent: nextQty } : i
      )));
      return true;
    }

    setSendItems((prev) => [...prev, {
      _id: product._id,
      name: product.name,
      sku: product.sku || product.externalId,
      quantityRequested: requestedMap.get(String(product._id)) || 0,
      quantitySent: 1,
      availableStock,
    }]);
    return true;
  };

  const handleGetProduct = async (rawInputOverride) => {
    const submittedInput = (typeof rawInputOverride === 'string' ? rawInputOverride : skuInput)?.trim();
    if (!submittedInput || gettingItem) return;

    const parsed = parseProductScanInput(submittedInput);
    if (!parsed) return;

    setGettingItem(true);
    try {
      const vendorId = bazaar?.vendor?._id || bazaar?.vendor;
      const params = { vendorId, page: 1, limit: 1 };
      if (parsed.productId) {
        params.productId = parsed.productId;
      } else {
        params.sku = parsed.sku;
      }

      const response = await getRequest(params, undefined, token, endpoints.imsAdmin.products);
      const product = response.data?.products?.docs?.[0];
      if (!product) {
        notification.error({ message: 'Product not found' });
        return;
      }

      if (addProductToSendList(product)) {
        notification.success({ message: 'Product added' });
        setSkuInput('');
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Product not found' });
    } finally {
      setGettingItem(false);
      setScanning(false);
    }
  };

  const handleParseInput = (value) => {
    setSkuInput(value);
  };

  const handleQrScan = (data) => {
    if (!scanning && !gettingItem && data) {
      setScanning(true);
      handleGetProduct(data);
    }
  };

  const updateQty = (productId, qty) => {
    setSendItems((prev) => prev.map((i) => {
      if (i._id !== productId) return i;
      const parsed = parseInt(qty, 10) || 0;
      const capped = Math.min(Math.max(parsed, 0), i.availableStock);
      return { ...i, quantitySent: capped };
    }));
  };

  const handleSubmit = () => {
    if (!sendItems.length) {
      notification.error({ message: 'Add at least one item' });
      return;
    }
    const overStock = sendItems.find((i) => i.quantitySent > i.availableStock);
    if (overStock) {
      notification.error({
        message: `Cannot send ${overStock.quantitySent} of ${overStock.sku} — only ${overStock.availableStock} available`,
      });
      return;
    }
    sendBazaarStock(id, sendItems.map((i) => ({
      _id: i._id,
      quantitySent: i.quantitySent,
      quantityRequested: i.quantityRequested,
    })), navigate);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(`/ims/admin/bazaars/${id}`)} sx={{ mb: 2, ...sec_button }}>
        Back to bazaar
      </Button>
      <Typography variant="h5">Send Stock — {bazaar?.name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Available quantities reflect main warehouse stock.
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Requested (reference)</Typography>
            <Table size="small" pagination={false}
              dataSource={(bazaar?.lineItems || []).map((li, i) => ({
                key: i,
                sku: li.product?.sku || li.product?.externalId,
                name: li.product?.name,
                qty: li.quantityRequested,
                available: getAvailableStock(li.product?._id || li.product),
              }))}
              columns={[
                { title: 'SKU', dataIndex: 'sku' },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Requested', dataIndex: 'qty' },
                {
                  title: 'Available',
                  dataIndex: 'available',
                  render: (val) => (
                    <Chip size="small" color={val > 0 ? 'success' : 'error'} label={val} />
                  ),
                },
              ]}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Items to send</Typography>
            <Card sx={{ width: '100%', mt: 2, mb: 2 }}>
              {isMobile ? (
                <CardContent>
                  <Box sx={{ padding: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <QrReader
                      onScan={handleQrScan}
                      style={{ height: 250, width: 250 }}
                      facingMode="environment"
                    />
                    <Button
                      onClick={() => setScanning(false)}
                      disabled={!scanning}
                      sx={{ ...main_button, mt: 1 }}
                    >
                      Scan Again
                    </Button>
                  </Box>
                </CardContent>
              ) : (
                <CardContent>
                  <Box sx={{ ...left_flex_box }}>
                    <MainInput
                      sx={{ marginInlineEnd: 2, minWidth: '50vw' }}
                      autoFocus
                      value={skuInput}
                      label="Scan / enter SKU or product QR"
                      onChange={({ target }) => handleParseInput(target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Tab' || e.key === 'Enter') {
                          e.preventDefault();
                          handleGetProduct(e.currentTarget.value);
                        }
                      }}
                    />
                    <Button
                      onClick={() => handleGetProduct()}
                      disabled={gettingItem}
                      sx={{ ...main_button, mt: 1 }}
                    >
                      {gettingItem ? <CircularProgress size={22} color="inherit" /> : 'Add item'}
                    </Button>
                  </Box>
                </CardContent>
              )}
            </Card>
            <Table size="small" pagination={false}
              dataSource={sendItems.map((i) => ({ ...i, key: i._id }))}
              columns={[
                { title: 'SKU', dataIndex: 'sku' },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Requested', dataIndex: 'quantityRequested' },
                {
                  title: 'Available',
                  dataIndex: 'availableStock',
                  render: (val, row) => (
                    <Chip
                      size="small"
                      color={row.quantitySent > val ? 'error' : val > 0 ? 'success' : 'default'}
                      label={val}
                    />
                  ),
                },
                {
                  title: 'Sending',
                  render: (_, row) => (
                    <MainInput type="number" value={row.quantitySent}
                      onChange={(e) => updateQty(row._id, e.target.value)}
                      inputProps={{ min: 1, max: row.availableStock }}
                      error={row.quantitySent > row.availableStock}
                    />
                  ),
                },
                {
                  title: 'Variance',
                  render: (_, row) => {
                    const v = row.quantitySent - row.quantityRequested;
                    return <Chip size="small" color={v === 0 ? 'success' : 'warning'} label={v > 0 ? `+${v}` : v} />;
                  },
                },
              ]}
            />
            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button sx={sec_button} onClick={() => navigate(`/ims/admin/bazaars/${id}`)}>Cancel</Button>
              <Button sx={main_button} onClick={handleSubmit} disabled={isWaiting}>Confirm Send</Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  bazaarData: state.records,
  token: state.auth.token,
  isWaiting: state.wait.isLoading,
});

export default connect(mapStateToProps, { getBazaar, sendBazaarStock })(BazaarSendStock);
